import React, { useState, useMemo, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PP = "'Poppins', sans-serif";
const C = {
  blue700: '#265F97', blue500: '#5E99D2', blue300: '#9EC2E5', blue100: '#DEEBF7',
  gray700: '#3D4248', gray500: '#5E666E', gray100: '#D7D9DB', gray50: '#F3F4F4',
  lima: '#DCDE74',
  blanco: '#FFFFFF',
  rojo: '#DC2626',
};

const PAQUETES = [
  {
    id: 'esencial',
    nombre: 'Presencia · Esencial',
    nombreCorto: 'Esencial',
    disenos: 4,
    precio: 1799,
    headline: 'Mantén tus pantallas vivas con contenido fresco cada mes.',
    desc: 'Para marcas que quieren empezar a profesionalizar sus pantallas sin complicarse. Nosotros convertimos tus promos en piezas con tu identidad y las publicamos en toda tu red.',
    incluye: [
      '4 diseños nuevos al mes (imagen fija)',
      'Aplicación de tu identidad visual',
      'Publicación y programación en toda la red',
      '1 ronda de ajustes por pieza',
      'Entrega en 5 días hábiles',
    ],
    color: C.blue300,
  },
  {
    id: 'activo',
    nombre: 'Movimiento · Activo',
    nombreCorto: 'Activo',
    disenos: 8,
    precio: 2599,
    headline: 'Combina imagen y video para captar la atención en tienda.',
    desc: 'Para marcas que entienden el contenido como un medio, no como adorno. Mezclamos piezas fijas con video y animación, planificamos un calendario mensual, y te dejamos plantillas reutilizables.',
    incluye: [
      '8 diseños nuevos al mes (fija + video / animación)',
      'Calendario editorial mensual planificado',
      'Plantillas editables reutilizables',
      '2 rondas de ajustes por pieza',
      'Entrega prioritaria en 3 días hábiles',
    ],
    destacado: true,
    color: C.blue500,
  },
  {
    id: 'premium',
    nombre: 'Marca total · Premium',
    nombreCorto: 'Premium',
    disenos: 16,
    precio: 5900,
    headline: 'Tus pantallas como un canal de marca de verdad.',
    desc: 'Para marcas que tratan sus pantallas como un medio propio y buscan máxima producción, calidad y agilidad. Producción amplia, motion graphics, biblioteca de plantillas a medida y reporte mensual.',
    incluye: [
      '16 diseños nuevos al mes (fija + video + motion graphics)',
      'Calendario editorial estratégico',
      'Biblioteca de plantillas exclusivas',
      'Ajustes ilimitados razonables',
      'Reporte mensual de pantallas activas',
      'Entrega exprés en 48 horas',
    ],
    color: C.blue700,
  },
];

const TABS = [
  { id: 'calculadora', label: 'Calculadora' },
  { id: 'clientes', label: 'Clientes activos', fase: 'F4', desc: 'Panel de clientes que firmaron contrato, con sus sucursales, cuánto cobras al mes (MRR/ARR), historial.' },
  { id: 'facturacion', label: 'Facturación', fase: 'F5', desc: 'Cuentas por cobrar, pagos a Xibo, margen real por cliente.' },
  { id: 'configuracion', label: 'Configuración', fase: 'F2', desc: 'Editar precios e información de los paquetes desde la interfaz.' },
  { id: 'xibo', label: 'Integración Xibo', fase: 'F6', desc: 'Cargar contenido al CMS de pantallas desde Beep-OS.' },
];

const STORAGE_KEY = 'beeptv_cotizacion_borrador';
const freshCliente = () => ({ nombre: '', contacto: '', sucursales: [{ id: 1, nombre: 'Sucursal 1', pantallas: 1 }] });
const freshSistema = () => ({ duracion: 1, precioBase: 250, descuento: 0 });
const freshPaquete = () => ({ activado: false, seleccionado: null, precios: { esencial: 1799, activo: 2599, premium: 5900 }, precioEditado: null, descuento: 0 });

const COSTOS_DEFAULT = {
  sistemaPorPantalla: 100,
  paqueteEsencial: 200,
  paqueteActivo: 400,
  paquetePremium: 800,
};

const COSTOS_STORAGE_KEY = 'beeptv_costos_internos';

// Migracion de keys viejas (BeepScreen) a nuevas (BeepTV)
(function migrateBeepScreenKeys() {
  const migraciones = [
    ['beepscreen_cotizacion_borrador', 'beeptv_cotizacion_borrador'],
    ['beepscreen_costos_internos', 'beeptv_costos_internos'],
  ];
  migraciones.forEach(([viejaKey, nuevaKey]) => {
    try {
      const valorViejo = localStorage.getItem(viejaKey);
      const valorNuevo = localStorage.getItem(nuevaKey);
      if (valorViejo !== null && valorNuevo === null) {
        localStorage.setItem(nuevaKey, valorViejo);
        localStorage.removeItem(viejaKey);
      }
    } catch {}
  });
})();

const DURACIONES = [
  { id: 1,  label: '1 mes',    meses: 1,  descuento: 0,  destacado: false },
  { id: 3,  label: '3 meses',  meses: 3,  descuento: 5,  destacado: false },
  { id: 6,  label: '6 meses',  meses: 6,  descuento: 8,  destacado: false },
  { id: 12, label: '12 meses', meses: 12, descuento: 12, destacado: true  },
];

function fmtMXN(n) {
  const s = Math.abs(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (n < 0 ? '-' : '') + '$' + s + ' MXN';
}

function fmtFecha(d) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function ProximamenteCard({ fase, descripcion }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: 300, padding: 40, background: C.blue100, borderRadius: 16,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.6 }}>📺</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.blue700, marginBottom: 8 }}>
        Próximamente · {fase}
      </div>
      <div style={{ fontSize: 14, color: C.gray500, maxWidth: 420, lineHeight: 1.6 }}>
        {descripcion}
      </div>
    </div>
  );
}

function IVADesglose({ total, fontSize, showTotal }) {
  const sinIVA = total / 1.16;
  const iva = total - sinIVA;
  const sz = fontSize || 11;
  return (
    <>
      <div style={{ height: 1, background: C.blue300, margin: '6px 0 4px' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: sz, color: C.gray500 }}>
        <span>Subtotal sin IVA</span>
        <span>{fmtMXN(sinIVA)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: sz, color: C.gray500 }}>
        <span>IVA (16%)</span>
        <span>{fmtMXN(iva)}</span>
      </div>
      {showTotal && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: C.blue700, fontSize: sz + 1 }}>
          <span>Total con IVA</span>
          <span>{fmtMXN(total)}</span>
        </div>
      )}
    </>
  );
}

function BeepTV({ esCelular, usuario }) {
  const [cliente, setCliente] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved?.cliente && Array.isArray(saved.cliente.sucursales)) return saved.cliente;
    } catch {}
    return freshCliente();
  });
  const [sistema, setSistema] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved?.sistema) {
        if ('periodicidad' in saved.sistema) {
          return {
            duracion: saved.sistema.periodicidad === 'anual' ? 12 : 1,
            precioBase: saved.sistema.precioMensual || 250,
            descuento: saved.sistema.descuento || 0,
          };
        }
        if ('duracion' in saved.sistema) return saved.sistema;
      }
    } catch {}
    return freshSistema();
  });
  const [paquete, setPaquete] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved?.paquete && typeof saved.paquete.activado === 'boolean') return saved.paquete;
    } catch {}
    return freshPaquete();
  });
  const [folio] = useState(() => `BTV-2026-${String(Math.floor(Math.random() * 9000) + 1000)}`);
  const [tabActiva, setTabActiva] = useState('calculadora');
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [costos, setCostos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COSTOS_STORAGE_KEY));
      if (saved && typeof saved === 'object') {
        return { ...COSTOS_DEFAULT, ...saved };
      }
    } catch {}
    return COSTOS_DEFAULT;
  });
  const [editandoCostos, setEditandoCostos] = useState(false);
  const [costosEnEdicion, setCostosEnEdicion] = useState(costos);

  useEffect(() => {
    try {
      localStorage.setItem(COSTOS_STORAGE_KEY, JSON.stringify(costos));
    } catch {}
  }, [costos]);

  const refNombre = useRef(null);
  const refContacto = useRef(null);
  const refsSucursales = useRef({});
  const refPaquetes = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ cliente, sistema, paquete }));
    } catch {}
  }, [cliente, sistema, paquete]);

  const setC = (k, v) => setCliente(p => ({ ...p, [k]: v }));
  const setS = (k, v) => setSistema(p => ({ ...p, [k]: v }));
  const setP = (k, v) => setPaquete(p => ({ ...p, [k]: v }));

  const agregarSucursal = () => {
    setCliente(p => {
      const maxId = p.sucursales.reduce((max, s) => Math.max(max, s.id), 0);
      return {
        ...p,
        sucursales: [...p.sucursales, { id: maxId + 1, nombre: `Sucursal ${maxId + 1}`, pantallas: 1 }],
      };
    });
  };

  const eliminarSucursal = (id) => {
    setCliente(p => {
      if (p.sucursales.length <= 1) return p;
      return { ...p, sucursales: p.sucursales.filter(s => s.id !== id) };
    });
  };

  const actualizarSucursal = (id, campo, valor) => {
    setCliente(p => ({
      ...p,
      sucursales: p.sucursales.map(s => s.id === id ? { ...s, [campo]: valor } : s),
    }));
  };

  const seleccionarPaquete = (paq) => {
    setPaquete(p => ({
      ...p,
      seleccionado: paq.id,
      precioEditado: p.precios[paq.id] || paq.precio,
      descuento: 0,
    }));
  };

  const limpiarCotizacion = () => {
    if (!window.confirm('¿Limpiar todos los datos de la cotización?')) return;
    localStorage.removeItem(STORAGE_KEY);
    setCliente(freshCliente());
    setSistema(freshSistema());
    setPaquete(freshPaquete());
    setErroresValidacion([]);
  };

  const intentarGenerarPDF = () => {
    const errores = [];
    let primerCampoConError = null;

    if (!cliente.nombre.trim()) {
      errores.push('Nombre del cliente o empresa');
      if (!primerCampoConError) primerCampoConError = refNombre.current;
    }
    if (!cliente.contacto.trim()) {
      errores.push('Nombre de contacto');
      if (!primerCampoConError) primerCampoConError = refContacto.current;
    }
    const sucursalesInvalidas = cliente.sucursales.filter(s => !s.nombre.trim() || !s.pantallas || s.pantallas < 1);
    if (sucursalesInvalidas.length > 0) {
      errores.push('Todas las sucursales deben tener nombre y al menos 1 pantalla');
      if (!primerCampoConError) {
        const primeraInvalida = sucursalesInvalidas[0];
        primerCampoConError = refsSucursales.current[primeraInvalida.id];
      }
    }
    if (paquete.activado && !paquete.seleccionado) {
      errores.push('Selecciona un paquete de diseño o desactiva el switch');
      if (!primerCampoConError) primerCampoConError = refPaquetes.current;
    }

    if (errores.length > 0) {
      setErroresValidacion(errores);
      if (primerCampoConError) {
        primerCampoConError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          if (primerCampoConError.focus) primerCampoConError.focus();
        }, 400);
      }
      return;
    }
    setErroresValidacion([]);
    generarPDF();
  };

  const calc = useMemo(() => {
    const totalSucursales = cliente.sucursales.length;
    const totalPantallas = cliente.sucursales.reduce((sum, s) => sum + (s.pantallas || 0), 0);

    const duracionObj = DURACIONES.find(d => d.id === sistema.duracion) || DURACIONES[0];
    const meses = duracionObj.meses;
    const dtoDuracion = duracionObj.descuento;
    const periodoLabel = meses === 1 ? 'mensual' : meses === 12 ? 'anual' : `${meses} meses`;

    const precioConDtoDuracion = sistema.precioBase * (1 - dtoDuracion / 100);
    const factorDtoManual = 1 - sistema.descuento / 100;
    const precioFinalPorLicenciaMes = precioConDtoDuracion * factorDtoManual;

    const subtotalSistema = totalPantallas * precioFinalPorLicenciaMes * meses;
    const subtotalSistemaMes = totalPantallas * precioFinalPorLicenciaMes;
    const subtotalSistemaSinDto = totalPantallas * sistema.precioBase * meses;

    const ahorroDuracion = totalPantallas * sistema.precioBase * (dtoDuracion / 100) * meses;
    const ahorroManual = totalPantallas * precioConDtoDuracion * (sistema.descuento / 100) * meses;
    const ahorroTotal = ahorroDuracion + ahorroManual;

    let paqMes = 0;
    let paqNombre = '';
    let paqPrecioEditado = 0;
    let paqPrecioAplicado = 0;
    let paqObj = null;
    let paqSinDto = 0;
    let descuentoPaq = 0;
    if (paquete.activado && paquete.seleccionado) {
      paqObj = PAQUETES.find(p => p.id === paquete.seleccionado);
      paqPrecioEditado = paquete.precioEditado || 0;
      paqPrecioAplicado = paqPrecioEditado * (1 - paquete.descuento / 100);
      paqSinDto = paqPrecioEditado;
      descuentoPaq = paqSinDto - paqPrecioAplicado;
      paqMes = paqPrecioAplicado;
      paqNombre = paqObj ? paqObj.nombreCorto : '';
    }

    const paqPeriodoActual = paqMes * meses;
    const totalPeriodoActual = subtotalSistema + paqPeriodoActual;
    const totalMes = subtotalSistemaMes + paqMes;
    const totalAnual = totalMes * 12;

    const costoSistemaMes = totalPantallas * costos.sistemaPorPantalla;
    let costoPaqueteMes = 0;
    if (paquete.activado && paquete.seleccionado) {
      if (paquete.seleccionado === 'esencial') costoPaqueteMes = costos.paqueteEsencial;
      else if (paquete.seleccionado === 'activo') costoPaqueteMes = costos.paqueteActivo;
      else if (paquete.seleccionado === 'premium') costoPaqueteMes = costos.paquetePremium;
    }
    const costoTotalMes = costoSistemaMes + costoPaqueteMes;
    const ingresoSinIVAMensual = (subtotalSistemaMes + paqMes) / 1.16;
    const utilidadMensual = ingresoSinIVAMensual - costoTotalMes;
    const utilidadAnual = utilidadMensual * 12;
    const margenPorcentaje = ingresoSinIVAMensual > 0 ? (utilidadMensual / ingresoSinIVAMensual) * 100 : 0;

    const costoSistemaPeriodo = costoSistemaMes * meses;
    const costoPaquetePeriodo = costoPaqueteMes * meses;
    const costoTotalPeriodo = costoTotalMes * meses;
    const ingresoSinIVAPeriodo = ingresoSinIVAMensual * meses;
    const utilidadPeriodo = ingresoSinIVAPeriodo - costoTotalPeriodo;

    return {
      duracionObj, meses, dtoDuracion, periodoLabel,
      precioConDtoDuracion, precioFinalPorLicenciaMes,
      subtotalSistema, subtotalSistemaMes, subtotalSistemaSinDto,
      ahorroDuracion, ahorroManual, ahorroTotal,
      totalPantallas, totalSucursales,
      paqMes, paqNombre, paqPrecioEditado, paqPrecioAplicado, paqObj,
      paqSinDto, descuentoPaq,
      paqPeriodoActual, totalPeriodoActual, totalMes, totalAnual,
      costoSistemaMes, costoPaqueteMes, costoTotalMes,
      costoSistemaPeriodo, costoPaquetePeriodo, costoTotalPeriodo,
      ingresoSinIVAMensual, ingresoSinIVAPeriodo, utilidadMensual, utilidadAnual, utilidadPeriodo, margenPorcentaje,
    };
  }, [cliente.sucursales, sistema, paquete, costos]);

  const generarPDF = () => {
    const doc = new jsPDF('p', 'mm', 'letter');
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const margen = 18;
    const hoy = new Date();

    const ALTURA_HEADER = 25;
    const ALTURA_FOOTER = 18;
    const LIMITE_Y = H - ALTURA_FOOTER - 5;

    function dibujarHeader() {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(C.blue700);
      doc.text('BeepTV', margen, 15);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(C.gray500);
      doc.text(`Folio: ${folio}`, W - margen, 11, { align: 'right' });
      doc.text(`Fecha: ${fmtFecha(hoy)}`, W - margen, 15, { align: 'right' });
      const vigencia = new Date(hoy);
      vigencia.setDate(vigencia.getDate() + 15);
      doc.text(`Vigencia: 15 días (${fmtFecha(vigencia)})`, W - margen, 19, { align: 'right' });

      doc.setDrawColor(C.blue300);
      doc.setLineWidth(0.5);
      doc.line(margen, 22, W - margen, 22);
    }

    function dibujarFooter(pagina, totalPaginas) {
      doc.setDrawColor(C.gray100);
      doc.setLineWidth(0.3);
      doc.line(margen, H - ALTURA_FOOTER, W - margen, H - ALTURA_FOOTER);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(C.gray500);
      doc.text('Comercializadora Beep México', margen, H - 10);
      doc.text('Tel: (TBD)  ·  ventas@beepmexico.com  ·  beepmexico.com', margen, H - 6);
      doc.text(`Pág. ${pagina} / ${totalPaginas}`, W - margen, H - 6, { align: 'right' });
    }

    function verificarSalto(yActual, alturaNecesaria) {
      if (yActual + alturaNecesaria > LIMITE_Y) {
        doc.addPage();
        dibujarHeader();
        return ALTURA_HEADER + 5;
      }
      return yActual;
    }

    // Pagina 1 — Header
    dibujarHeader();
    let y = ALTURA_HEADER + 5;

    // A) Datos del cliente
    y = verificarSalto(y, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(C.gray700);
    doc.text('Datos del cliente', margen, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Nombre: ${cliente.nombre || '—'}`, margen, y); y += 5;
    doc.text(`Contacto: ${cliente.contacto || '—'}`, margen, y); y += 5;
    doc.text(`Sucursales: ${calc.totalSucursales}`, margen, y); y += 5;
    doc.text(`Pantallas totales: ${calc.totalPantallas}`, margen, y); y += 10;

    // B) Desglose por sucursal
    if (cliente.sucursales.length > 1) {
      y = verificarSalto(y, 15);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(C.gray700);
      doc.text('Desglose por sucursal', margen, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        head: [['#', 'Sucursal', 'Pantallas']],
        body: cliente.sucursales.map((s, i) => [String(i + 1), s.nombre, String(s.pantallas || 0)]),
        theme: 'grid',
        headStyles: { fillColor: C.blue300, textColor: C.gray700, fontSize: 8, font: 'helvetica', fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 8, textColor: C.gray700, font: 'helvetica' },
        columnStyles: { 0: { halign: 'center', cellWidth: 12 }, 2: { halign: 'center', cellWidth: 25 } },
        margin: {
          left: margen,
          right: margen,
          top: ALTURA_HEADER + 5,
          bottom: ALTURA_FOOTER + 5,
        },
        didDrawPage: function (data) {
          if (data.pageNumber > 1) {
            dibujarHeader();
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // C) Bloque vertical — Sistema
    const bw = W - margen * 2;
    const lx = margen + 4;
    const vx = W - margen - 4;

    const alturaSistema = (calc.dtoDuracion > 0 ? 6 : 0) + (sistema.descuento > 0 ? 6 : 0) + 55;
    y = verificarSalto(y, alturaSistema);

    doc.setFillColor(C.blue500);
    doc.rect(margen, y, bw, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor('#FFFFFF');
    doc.text('Sistema de signage BeepTV', lx, y + 5.5);
    y += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(C.gray700);
    doc.text('Duración:', lx, y);
    doc.text(`${calc.meses} ${calc.meses === 1 ? 'mes' : 'meses'}`, vx, y, { align: 'right' });
    y += 6;
    doc.text('Pantallas contratadas:', lx, y);
    doc.text(String(calc.totalPantallas), vx, y, { align: 'right' });
    y += 6;
    doc.text('Precio por licencia:', lx, y);
    doc.text(`${fmtMXN(calc.precioConDtoDuracion)} / mes`, vx, y, { align: 'right' });
    y += 6;

    if (calc.dtoDuracion > 0) {
      doc.setFontSize(8);
      doc.setTextColor('#16A34A');
      doc.text(`(incluye ${calc.dtoDuracion}% descuento por duración)`, lx, y);
      y += 5;
      doc.setFontSize(9);
    }

    if (sistema.descuento > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(C.blue700);
      doc.text(`Precio con descuento adicional (-${sistema.descuento}%):`, lx, y);
      doc.text(`${fmtMXN(calc.precioFinalPorLicenciaMes)} / mes`, vx, y, { align: 'right' });
      y += 6;
    }

    doc.setDrawColor(C.gray100);
    doc.setLineWidth(0.3);
    doc.line(lx, y, vx, y);
    y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(C.blue700);
    doc.text(`Subtotal (${calc.meses} ${calc.meses === 1 ? 'mes' : 'meses'}):`, lx, y);
    doc.text(fmtMXN(calc.subtotalSistema), vx, y, { align: 'right' });
    y += 8;

    // D) Bloque vertical — Paquete
    if (paquete.activado && calc.paqObj) {
      const alturaPaquete = (paquete.descuento > 0 ? 50 : 40) + (calc.paqObj.incluye.length * 4) + 15;
      y = verificarSalto(y, alturaPaquete);

      doc.setFillColor(C.blue500);
      doc.rect(margen, y, bw, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor('#FFFFFF');
      doc.text('Paquete de diseño', lx, y + 5.5);
      y += 12;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(C.gray700);
      doc.text('Paquete contratado:', lx, y);
      doc.text(calc.paqObj.nombre, vx, y, { align: 'right' });
      y += 6;
      doc.text('Diseños por mes:', lx, y);
      doc.text(String(calc.paqObj.disenos), vx, y, { align: 'right' });
      y += 6;

      doc.text('Precio mensual:', lx, y);
      doc.text(fmtMXN(calc.paqPrecioEditado), vx, y, { align: 'right' });
      y += 6;

      if (paquete.descuento > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(C.blue700);
        doc.text(`Precio con descuento (-${paquete.descuento}%):`, lx, y);
        doc.text(`${fmtMXN(calc.paqPrecioAplicado)} / mes`, vx, y, { align: 'right' });
        y += 6;
      }

      y += 4;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(C.gray500);
      doc.text('Incluye:', lx, y); y += 4;
      doc.setFont('helvetica', 'normal');
      calc.paqObj.incluye.forEach(item => {
        doc.text(`• ${item}`, lx + 2, y); y += 4;
      });
      y += 6;
    }

    // E) Recuadro Total + IVA
    y = verificarSalto(y, 45);

    const totalSinIVAPDF = calc.totalPeriodoActual / 1.16;
    const totalIVAPDF = calc.totalPeriodoActual - totalSinIVAPDF;
    const boxH = 36;

    doc.setFillColor(C.blue100);
    doc.roundedRect(margen, y, W - margen * 2, boxH, 3, 3, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(C.gray500);
    doc.text('Subtotal sin IVA:', margen + 6, y + 8);
    doc.text(fmtMXN(totalSinIVAPDF), W - margen - 6, y + 8, { align: 'right' });
    doc.text('IVA (16%):', margen + 6, y + 14);
    doc.text(fmtMXN(totalIVAPDF), W - margen - 6, y + 14, { align: 'right' });

    doc.setDrawColor(C.blue300);
    doc.setLineWidth(0.3);
    doc.line(margen + 4, y + 18, W - margen - 4, y + 18);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(C.blue700);
    doc.text(`Total a pagar (${calc.periodoLabel}):`, margen + 6, y + 27);
    doc.text(fmtMXN(calc.totalPeriodoActual), W - margen - 6, y + 27, { align: 'right' });

    y += boxH + 8;

    // F) Nota legal
    y = verificarSalto(y, 15);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(C.gray500);
    const nota = 'Esta cotización no incluye equipos, instalación, cableado ni mantenimiento físico de las pantallas. Esos conceptos se cotizan por separado. Precios en MXN, IVA incluido. Vigencia: 15 días.';
    const lineasNota = doc.splitTextToSize(nota, W - margen * 2);
    doc.text(lineasNota, margen, y);

    // Footer en TODAS las paginas
    const totalPaginas = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPaginas; i++) {
      doc.setPage(i);
      dibujarFooter(i, totalPaginas);
    }

    const fechaArchivo = `${hoy.getFullYear()}${String(hoy.getMonth() + 1).padStart(2, '0')}${String(hoy.getDate()).padStart(2, '0')}`;
    doc.save(`Cotizacion_BeepTV_${folio}_${fechaArchivo}.pdf`);
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', border: `1px solid ${C.gray100}`,
    borderRadius: 8, fontSize: 13, fontFamily: PP, color: C.gray700,
    boxSizing: 'border-box', outline: 'none', background: C.blanco,
  };
  const labelStyle = { fontSize: 11, color: C.gray500, fontFamily: PP, marginBottom: 4, display: 'block', fontWeight: 500 };
  const cardBase = { background: C.blanco, borderRadius: 12, padding: esCelular ? 16 : 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' };
  const dtoAlerta = (v) => v > 10;

  const paqSelObj = paquete.seleccionado ? PAQUETES.find(p => p.id === paquete.seleccionado) : null;
  const periodoLabel = calc.periodoLabel;

  return (
    <div style={{ fontFamily: PP, maxWidth: 1200, margin: '0 auto' }}>
      {/* S1 — Header */}
      <div style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 28, fontWeight: 600, color: C.blue700 }}>BeepTV</span>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.gray700, marginBottom: 2 }}>Calculadora comercial</div>
        {tabActiva === 'calculadora' && (
          <p style={{ fontSize: 13, color: C.gray500, margin: 0, lineHeight: 1.5 }}>
            Configura el servicio de signage para tu cliente y genera la cotización en segundos.
          </p>
        )}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0, marginTop: 16, marginBottom: 20,
        borderBottom: `2px solid ${C.gray100}`,
        overflowX: 'auto',
      }}>
        {TABS.map(tab => {
          const activa = tabActiva === tab.id;
          return (
            <button key={tab.id} onClick={() => setTabActiva(tab.id)} style={{
              padding: esCelular ? '10px 12px' : '10px 20px',
              border: 'none',
              borderBottom: activa ? `3px solid ${C.blue500}` : '3px solid transparent',
              background: 'transparent',
              fontSize: esCelular ? 11 : 13,
              fontWeight: activa ? 700 : 400,
              color: activa ? C.blue500 : C.gray500,
              fontFamily: PP,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s',
              marginBottom: -2,
            }}>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tabActiva === 'calculadora' ? (
        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 340px', gap: 20, alignItems: 'start' }}>
          {/* COLUMNA IZQ — Configuración */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Banner de errores de validacion */}
            {erroresValidacion.length > 0 && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10,
                padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                <div style={{ fontSize: 18, lineHeight: 1 }}>⚠️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#991B1B', marginBottom: 6 }}>
                    Te falta capturar algunos datos
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#7F1D1D', lineHeight: 1.7 }}>
                    {erroresValidacion.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                </div>
                <button
                  onClick={() => setErroresValidacion([])}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, color: '#991B1B' }}
                  title="Cerrar"
                >×</button>
              </div>
            )}

            {/* Bloque A — Datos del cliente */}
            <div style={cardBase}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.blue700, marginBottom: 14 }}>Datos del cliente</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Nombre del cliente o empresa</label>
                  <input ref={refNombre} value={cliente.nombre} onChange={e => setC('nombre', e.target.value)} style={{ ...inputStyle, borderColor: erroresValidacion.length > 0 && !cliente.nombre.trim() ? '#EF4444' : C.gray100 }} placeholder="Ej: Farmacias del Norte" />
                </div>
                <div>
                  <label style={labelStyle}>Nombre de contacto</label>
                  <input ref={refContacto} value={cliente.contacto} onChange={e => setC('contacto', e.target.value)} style={{ ...inputStyle, borderColor: erroresValidacion.length > 0 && !cliente.contacto.trim() ? '#EF4444' : C.gray100 }} placeholder="Ej: Carlos López" />
                </div>

                {/* Sucursales y pantallas */}
                <div>
                  <label style={labelStyle}>Sucursales y pantallas</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {cliente.sucursales.map((suc) => (
                      <div key={suc.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          ref={el => { if (el) refsSucursales.current[suc.id] = el; }}
                          value={suc.nombre}
                          onChange={e => actualizarSucursal(suc.id, 'nombre', e.target.value)}
                          style={{ ...inputStyle, flex: 1, borderColor: erroresValidacion.length > 0 && !suc.nombre.trim() ? '#EF4444' : C.gray100 }}
                          placeholder="Nombre de sucursal"
                        />
                        <input
                          type="number"
                          min="1"
                          value={suc.pantallas}
                          onChange={e => actualizarSucursal(suc.id, 'pantallas', Math.max(1, Number(e.target.value)))}
                          style={{ ...inputStyle, width: 80, flex: 'none', textAlign: 'center' }}
                          title="Pantallas"
                        />
                        <button
                          onClick={() => eliminarSucursal(suc.id)}
                          disabled={cliente.sucursales.length <= 1}
                          style={{
                            width: 32, height: 32, borderRadius: 8, border: 'none',
                            background: cliente.sucursales.length <= 1 ? C.gray50 : `${C.rojo}15`,
                            color: cliente.sucursales.length <= 1 ? C.gray100 : C.rojo,
                            fontSize: 16, cursor: cliente.sucursales.length <= 1 ? 'not-allowed' : 'pointer',
                            fontFamily: PP, fontWeight: 600, flex: 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          title="Eliminar sucursal"
                        >×</button>
                      </div>
                    ))}
                    <button onClick={agregarSucursal} style={{
                      padding: '8px 14px', border: `1px dashed ${C.blue300}`,
                      borderRadius: 8, background: 'transparent',
                      color: C.blue500, fontSize: 12, fontWeight: 600,
                      fontFamily: PP, cursor: 'pointer',
                    }}>+ Agregar sucursal</button>
                  </div>
                  <div style={{ fontSize: 11, color: C.gray500, marginTop: 6 }}>
                    {calc.totalSucursales} sucursal{calc.totalSucursales !== 1 ? 'es' : ''} · {calc.totalPantallas} pantalla{calc.totalPantallas !== 1 ? 's' : ''} en total
                  </div>
                </div>
              </div>
            </div>

            {/* Bloque B — Sistema BeepTV */}
            <div style={cardBase}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.blue700, marginBottom: 2 }}>Costo de licencias por pantalla</div>
              <div style={{ fontSize: 12, color: C.gray500, marginBottom: 14, lineHeight: 1.4 }}>Cada pantalla requiere una licencia activa. Plataforma de gestión y reproducción de contenido en pantallas.</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Duración del compromiso</label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: 8, marginTop: 6,
                  }}>
                    {DURACIONES.map(dur => {
                      const seleccionada = sistema.duracion === dur.id;
                      const precioConDto = sistema.precioBase * (1 - dur.descuento / 100);
                      return (
                        <div key={dur.id} onClick={() => setS('duracion', dur.id)} style={{
                          position: 'relative',
                          border: seleccionada ? `2px solid ${C.blue500}` : `1px solid ${C.gray100}`,
                          borderRadius: 10, padding: 12, cursor: 'pointer',
                          background: seleccionada ? `${C.blue500}0A` : C.blanco,
                          textAlign: 'center', transition: 'all 0.15s',
                        }}>
                          {dur.destacado && (
                            <div style={{
                              position: 'absolute', top: -1, right: 8,
                              background: C.lima, color: C.gray700, fontSize: 9, fontWeight: 700,
                              padding: '2px 8px', borderRadius: '0 0 6px 6px',
                            }}>Mejor valor</div>
                          )}
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.blue700, marginBottom: 4 }}>{dur.label}</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: C.gray700, marginBottom: 2 }}>{fmtMXN(precioConDto)}</div>
                          <div style={{ fontSize: 10, color: C.gray500, marginBottom: 6 }}>/ mes</div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: dur.descuento > 0 ? '#16A34A' : C.gray500, marginBottom: 6 }}>
                            {dur.descuento > 0 ? `Ahorra ${dur.descuento}%` : 'Sin descuento'}
                          </div>
                          <div style={{ fontSize: 10, color: C.gray500, borderTop: `1px solid ${C.gray100}`, paddingTop: 4 }}>
                            Total: {fmtMXN(calc.totalPantallas * precioConDto * dur.meses)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Precio por licencia / mes (MXN)</label>
                  <input type="number" min="0" value={sistema.precioBase} onChange={e => setS('precioBase', Number(e.target.value))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Descuento adicional del comercial %</label>
                  <input type="number" min="0" max="100" value={sistema.descuento}
                    onChange={e => setS('descuento', Math.min(100, Math.max(0, Number(e.target.value))))}
                    style={{ ...inputStyle, borderColor: dtoAlerta(sistema.descuento) ? C.rojo : C.gray100 }} />
                  {dtoAlerta(sistema.descuento) && (
                    <div style={{ fontSize: 10, color: C.rojo, marginTop: 4, fontWeight: 500 }}>Requiere aprobación de dirección</div>
                  )}
                </div>

                {/* Caja Sistema — desglose con 2 capas de descuento + IVA */}
                <div style={{ padding: '10px 12px', background: C.blue100, borderRadius: 8, fontSize: 12, color: C.gray700, lineHeight: 1.8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Precio por licencia base</span>
                    <span>{fmtMXN(sistema.precioBase)} / mes</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>N° de licencias</span>
                    <span>{calc.totalPantallas} pantalla{calc.totalPantallas !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Duración</span>
                    <span>{calc.meses} {calc.meses === 1 ? 'mes' : 'meses'}</span>
                  </div>

                  {calc.dtoDuracion > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#16A34A', fontWeight: 600 }}>
                      <span>Descuento por duración (-{calc.dtoDuracion}%)</span>
                      <span>aplicado</span>
                    </div>
                  )}
                  {sistema.descuento > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.blue700, fontWeight: 600 }}>
                      <span>Descuento comercial (-{sistema.descuento}%)</span>
                      <span>aplicado</span>
                    </div>
                  )}

                  <div style={{ height: 1, background: C.blue300, margin: '4px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: C.blue700 }}>
                    <span>Precio final por licencia / mes</span>
                    <span>{fmtMXN(calc.precioFinalPorLicenciaMes)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: C.blue700, fontSize: 14 }}>
                    <span>TOTAL del periodo ({calc.meses} {calc.meses === 1 ? 'mes' : 'meses'})</span>
                    <span>{fmtMXN(calc.subtotalSistema)}</span>
                  </div>

                  {calc.meses > 1 && (
                    <div style={{ fontSize: 11, color: C.gray500, textAlign: 'right', marginTop: 2 }}>
                      Equivalente: {fmtMXN(calc.subtotalSistemaMes)} / mes
                    </div>
                  )}

                  <IVADesglose total={calc.subtotalSistema} showTotal />
                </div>
              </div>
            </div>

            {/* Bloque C — Paquete de diseño */}
            <div ref={refPaquetes} style={cardBase}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.blue700 }}>Paquete de diseño · add-on opcional</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: C.gray500, userSelect: 'none' }}>
                  Activar
                  <div onClick={() => setP('activado', !paquete.activado)} style={{
                    width: 40, height: 22, borderRadius: 11, background: paquete.activado ? C.blue500 : C.gray100,
                    position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                  }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%', background: C.blanco,
                      position: 'absolute', top: 2, left: paquete.activado ? 20 : 2,
                      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                </label>
              </div>
              <div style={{ fontSize: 12, color: C.gray500, marginBottom: 14, lineHeight: 1.4 }}>Producción mensual de piezas con identidad de marca para tus pantallas.</div>

              {paquete.activado && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {PAQUETES.map(paq => {
                      const sel = paquete.seleccionado === paq.id;
                      return (
                        <div key={paq.id} onClick={() => seleccionarPaquete(paq)} style={{
                          border: sel ? `2px solid ${paq.color}` : `1px solid ${C.gray100}`,
                          borderRadius: 12, padding: esCelular ? 14 : 18, cursor: 'pointer',
                          background: sel ? `${paq.color}0A` : C.blanco,
                          position: 'relative', transition: 'all 0.15s',
                        }}>
                          {paq.destacado && (
                            <div style={{
                              position: 'absolute', top: -1, right: 16,
                              background: C.lima, color: C.gray700, fontSize: 10, fontWeight: 700,
                              padding: '3px 10px', borderRadius: '0 0 6px 6px',
                            }}>Más elegido</div>
                          )}
                          <div style={{ fontSize: 11, color: paq.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                            {paq.nombre}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.gray700, marginBottom: 4, lineHeight: 1.4 }}>
                            {paq.headline}
                          </div>
                          <div style={{ fontSize: 12, color: C.gray500, marginBottom: 10, lineHeight: 1.5 }}>
                            {paq.desc}
                          </div>
                          <div style={{ fontSize: 12, color: C.gray500, marginBottom: 8, lineHeight: 1.6 }}>
                            {paq.incluye.map((item, i) => (
                              <div key={i} style={{ paddingLeft: 12, textIndent: -12 }}>✓ {item}</div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                            <span style={{ fontSize: 20, fontWeight: 800, color: paq.color }}>{fmtMXN(paq.precio)}</span>
                            <span style={{ fontSize: 11, color: C.gray500 }}>/ mes</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {paqSelObj && (
                    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label style={labelStyle}>Precio del paquete / mes (MXN)</label>
                        <input type="number" min="0" value={paquete.precioEditado || ''} onChange={e => setP('precioEditado', Number(e.target.value))} style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Descuento %</label>
                        <input type="number" min="0" max="100" value={paquete.descuento}
                          onChange={e => setP('descuento', Math.min(100, Math.max(0, Number(e.target.value))))}
                          style={{ ...inputStyle, borderColor: dtoAlerta(paquete.descuento) ? C.rojo : C.gray100 }} />
                        {dtoAlerta(paquete.descuento) && (
                          <div style={{ fontSize: 10, color: C.rojo, marginTop: 4, fontWeight: 500 }}>Requiere aprobación de dirección</div>
                        )}
                      </div>

                      {/* Caja Paquete — estilo factura + IVA */}
                      <div style={{ padding: '8px 12px', background: C.blue100, borderRadius: 8, fontSize: 12, color: C.gray700, lineHeight: 1.8 }}>
                        {paquete.descuento > 0 ? (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Precio paquete</span>
                              <span>{fmtMXN(calc.paqSinDto)} / mes</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: C.blue700 }}>
                              <span>Precio con descuento (-{paquete.descuento}%)</span>
                              <span>{fmtMXN(calc.paqPrecioAplicado)} / mes</span>
                            </div>
                          </>
                        ) : (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                            <span>Precio paquete</span>
                            <span>{fmtMXN(calc.paqPrecioAplicado)} / mes</span>
                          </div>
                        )}

                        <IVADesglose total={calc.paqPrecioAplicado} showTotal />
                      </div>
                    </div>
                  )}

                  <div style={{
                    marginTop: 14, padding: '10px 14px', background: `${C.lima}22`,
                    borderRadius: 8, fontSize: 11, color: C.gray500, lineHeight: 1.5,
                  }}>
                    <strong style={{ color: C.gray700 }}>Extras por campaña</strong> (cotización aparte):
                    temporada (Buen Fin, Navidad), lanzamientos de producto, video premium, contenido segmentado por región.
                  </div>
                </>
              )}
            </div>

            {/* Bloque D — Desglose de utilidad (solo direccion) */}
            {usuario && usuario.rol && (usuario.rol === 'direccion' || usuario.rol === 'Direccion' || usuario.rol === 'Dirección') && (
              <div style={{
                ...cardBase,
                background: C.gray50,
                border: `1px dashed ${C.gray100}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 18 }}>🔒</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.blue700 }}>Desglose de utilidad — vista interna</div>
                    <div style={{ fontSize: 11, color: C.gray500, marginTop: 2 }}>Visible solo para dirección. No aparece en el PDF del cliente.</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: 14 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Costos estimados
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: C.gray500 }}>Sistema ({calc.totalPantallas} pantallas × ${costos.sistemaPorPantalla})</span>
                        <span>{fmtMXN(calc.costoSistemaMes)}</span>
                      </div>
                      {calc.costoPaqueteMes > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: C.gray500 }}>Paquete diseño</span>
                          <span>{fmtMXN(calc.costoPaqueteMes)}</span>
                        </div>
                      )}
                      <div style={{ height: 1, background: C.gray100, margin: '4px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                        <span>Costo total / mes</span>
                        <span>{fmtMXN(calc.costoTotalMes)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: C.blue700 }}>
                        <span>Costo total ({calc.meses} {calc.meses === 1 ? 'mes' : 'meses'})</span>
                        <span>{fmtMXN(calc.costoTotalPeriodo)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Ingresos y utilidad
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: C.gray500 }}>Ingreso ({calc.meses} {calc.meses === 1 ? 'mes' : 'meses'}, sin IVA)</span>
                        <span>{fmtMXN(calc.ingresoSinIVAPeriodo)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: C.gray500 }}>Costo total ({calc.meses} {calc.meses === 1 ? 'mes' : 'meses'})</span>
                        <span>-{fmtMXN(calc.costoTotalPeriodo)}</span>
                      </div>
                      <div style={{ height: 1, background: C.gray100, margin: '4px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: C.blue700, fontSize: 15 }}>
                        <span>Utilidad del periodo ({calc.meses}m)</span>
                        <span>{fmtMXN(calc.utilidadPeriodo)}</span>
                      </div>
                      <div style={{ height: 1, background: C.gray100, margin: '4px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                        <span style={{ color: C.gray500 }}>Equivalente mensual</span>
                        <span>{fmtMXN(calc.utilidadMensual)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                        <span style={{ color: C.gray500 }}>Equivalente anual</span>
                        <span>{fmtMXN(calc.utilidadAnual)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                        <span style={{ color: C.gray500 }}>Margen</span>
                        <span style={{
                          color: calc.margenPorcentaje >= 50 ? '#16A34A' : calc.margenPorcentaje >= 30 ? '#CA8A04' : '#DC2626',
                        }}>
                          {calc.margenPorcentaje.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.gray100}` }}>
                  {!editandoCostos ? (
                    <button
                      onClick={() => { setCostosEnEdicion(costos); setEditandoCostos(true); }}
                      style={{
                        background: 'transparent', border: `1px solid ${C.gray100}`,
                        color: C.blue700, fontSize: 12, fontWeight: 600,
                        padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                        fontFamily: PP, display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >✏️ Editar costos</button>
                  ) : (
                    <div style={{ padding: 12, background: C.blanco, borderRadius: 8, border: `1px solid ${C.gray100}` }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.gray700, marginBottom: 10 }}>
                        Editar costos internos (MXN)
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: 10 }}>
                        <div>
                          <label style={{ ...labelStyle, fontSize: 11 }}>Sistema por pantalla / mes</label>
                          <input type="number" min="0" value={costosEnEdicion.sistemaPorPantalla}
                            onChange={e => setCostosEnEdicion(prev => ({ ...prev, sistemaPorPantalla: Number(e.target.value) || 0 }))}
                            style={{ ...inputStyle, fontSize: 12 }} />
                        </div>
                        <div>
                          <label style={{ ...labelStyle, fontSize: 11 }}>Paquete Esencial / mes</label>
                          <input type="number" min="0" value={costosEnEdicion.paqueteEsencial}
                            onChange={e => setCostosEnEdicion(prev => ({ ...prev, paqueteEsencial: Number(e.target.value) || 0 }))}
                            style={{ ...inputStyle, fontSize: 12 }} />
                        </div>
                        <div>
                          <label style={{ ...labelStyle, fontSize: 11 }}>Paquete Activo / mes</label>
                          <input type="number" min="0" value={costosEnEdicion.paqueteActivo}
                            onChange={e => setCostosEnEdicion(prev => ({ ...prev, paqueteActivo: Number(e.target.value) || 0 }))}
                            style={{ ...inputStyle, fontSize: 12 }} />
                        </div>
                        <div>
                          <label style={{ ...labelStyle, fontSize: 11 }}>Paquete Premium / mes</label>
                          <input type="number" min="0" value={costosEnEdicion.paquetePremium}
                            onChange={e => setCostosEnEdicion(prev => ({ ...prev, paquetePremium: Number(e.target.value) || 0 }))}
                            style={{ ...inputStyle, fontSize: 12 }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                          onClick={() => { setCostos(costosEnEdicion); setEditandoCostos(false); }}
                          style={{
                            background: C.blue500, color: C.blanco, border: 'none',
                            fontSize: 12, fontWeight: 600, padding: '8px 16px',
                            borderRadius: 8, cursor: 'pointer', fontFamily: PP,
                          }}
                        >Guardar costos</button>
                        <button
                          onClick={() => { setCostosEnEdicion(costos); setEditandoCostos(false); }}
                          style={{
                            background: 'transparent', color: C.gray500,
                            border: `1px solid ${C.gray100}`, fontSize: 12, fontWeight: 500,
                            padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: PP,
                          }}
                        >Cancelar</button>
                        <button
                          onClick={() => setCostosEnEdicion(COSTOS_DEFAULT)}
                          style={{
                            background: 'transparent', color: C.gray500, border: 'none',
                            fontSize: 11, fontWeight: 500, padding: '8px 12px',
                            cursor: 'pointer', fontFamily: PP, marginLeft: 'auto',
                            textDecoration: 'underline',
                          }}
                        >Restaurar valores por defecto</button>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 11, color: C.gray500, marginTop: 12, lineHeight: 1.5, fontStyle: 'italic' }}>
                  Costos actuales: ${costos.sistemaPorPantalla}/pantalla/mes · Paquetes: ${costos.paqueteEsencial} / ${costos.paqueteActivo} / ${costos.paquetePremium}. Edita arriba si los costos cambian.
                </div>
              </div>
            )}
          </div>

          {/* COLUMNA DER — Resumen */}
          <div style={{
            ...cardBase,
            border: `2px solid ${C.blue300}`,
            background: C.blue100,
            position: esCelular ? 'static' : 'sticky',
            top: esCelular ? undefined : 24,
            maxHeight: esCelular ? undefined : 'calc(100vh - 48px)',
            overflowY: esCelular ? 'visible' : 'auto',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.blue700, marginBottom: 14 }}>Resumen de cotización</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: C.gray700 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: C.gray500 }}>Cliente</span>
                <span style={{ fontWeight: 500 }}>{cliente.nombre || 'Sin definir'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: C.gray500 }}>Sucursales</span>
                <span style={{ fontWeight: 500 }}>{calc.totalSucursales}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: C.gray500 }}>Pantallas totales</span>
                <span style={{ fontWeight: 500 }}>{calc.totalPantallas}</span>
              </div>

              <div style={{ height: 1, background: C.blue300, margin: '4px 0' }} />

              {/* Sistema — mini-bloque */}
              <div style={{ background: C.blanco, borderRadius: 8, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: C.gray500 }}>Sistema BeepTV ({calc.meses === 1 ? '1 mes' : `${calc.meses} meses`})</span>
                  <span style={{ fontWeight: 600 }}>{fmtMXN(calc.subtotalSistema)}</span>
                </div>
                {calc.ahorroTotal > 0 && (
                  <div style={{ fontSize: 11, color: C.gray500, textAlign: 'right', marginTop: 2 }}>
                    Sin descuentos: {fmtMXN(calc.subtotalSistemaSinDto)} | Ahorro: {fmtMXN(calc.ahorroTotal)}
                  </div>
                )}
              </div>

              {/* Paquete — mini-bloque */}
              <div style={{ background: C.blanco, borderRadius: 8, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: C.gray500 }}>Paquete de diseño</span>
                  <span style={{ fontWeight: 500 }}>
                    {paquete.activado && calc.paqNombre
                      ? `${calc.paqNombre}: ${fmtMXN(calc.paqPeriodoActual)}`
                      : 'No incluido'}
                  </span>
                </div>
                {paquete.activado && paquete.descuento > 0 && (
                  <div style={{ fontSize: 11, color: C.gray500, textAlign: 'right', marginTop: 2 }}>
                    Precio antes del descuento: {fmtMXN(calc.paqPrecioEditado * calc.meses)} | Descuento: -{paquete.descuento}%
                  </div>
                )}
              </div>

              <div style={{ height: 1, background: C.blue300, margin: '4px 0' }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}>
                <span style={{ fontWeight: 700, color: C.blue700 }}>Total a pagar ({periodoLabel})</span>
                <span style={{ fontWeight: 800, color: C.blue700 }}>{fmtMXN(calc.totalPeriodoActual)}</span>
              </div>

              {/* IVA desglose en resumen */}
              <div style={{ fontSize: 11, color: C.gray500 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Subtotal sin IVA</span>
                  <span>{fmtMXN(calc.totalPeriodoActual / 1.16)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>IVA (16%)</span>
                  <span>{fmtMXN(calc.totalPeriodoActual - calc.totalPeriodoActual / 1.16)}</span>
                </div>
              </div>
            </div>

            {usuario && usuario.rol && (usuario.rol === 'direccion' || usuario.rol === 'Direccion' || usuario.rol === 'Dirección') && (
              <div style={{
                marginTop: 12, padding: 12, background: C.gray50,
                border: `1px dashed ${C.gray100}`, borderRadius: 10,
                fontSize: 12, color: C.gray700,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  🔒 Utilidad interna
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: C.blue700, fontSize: 13 }}>
                    <span>Utilidad mensual</span>
                    <span>{fmtMXN(calc.utilidadMensual)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500 }}>
                    <span style={{ color: C.gray500 }}>Utilidad anual</span>
                    <span>{fmtMXN(calc.utilidadAnual)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                    <span style={{ color: C.gray500 }}>Margen</span>
                    <span style={{
                      color: calc.margenPorcentaje >= 50 ? '#16A34A' : calc.margenPorcentaje >= 30 ? '#CA8A04' : '#DC2626',
                    }}>
                      {calc.margenPorcentaje.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div style={{ fontSize: 10, color: C.gray500, marginTop: 14, lineHeight: 1.5 }}>
              Esta cotización NO incluye equipos, instalación ni cableado. Esos conceptos se cotizan por separado según las necesidades específicas de cada sucursal. Precios con IVA incluido.
            </div>

            <button
              onClick={intentarGenerarPDF}
              style={{
                marginTop: 16, width: '100%', padding: '14px 20px',
                background: C.blue500,
                color: C.blanco,
                border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
                fontFamily: PP, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              Generar cotización PDF
            </button>

            <button
              onClick={limpiarCotizacion}
              style={{
                marginTop: 8, width: '100%', padding: '6px',
                background: 'transparent', border: 'none',
                color: C.gray500, fontSize: 11, fontFamily: PP,
                cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              Limpiar cotización
            </button>
          </div>
        </div>
      ) : (
        (() => {
          const tab = TABS.find(t => t.id === tabActiva);
          return tab && tab.fase ? <ProximamenteCard fase={tab.fase} descripcion={tab.desc} /> : null;
        })()
      )}
    </div>
  );
}

export default BeepTV;
